import React, { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getList } from "../../../services/service";
import { createProductFromAkfa, getAkfaProductDraft } from "../../../services/akfa/akfaService";
import { ARTICLE_GROUP_TYPES } from "../AkfaPositionPicker";
import { EMPTY_PRODUCT_FORM } from "../AkfaCreateProductForm";
import { formatPrice } from "../../../layout/akfa/akfaUtils";

const BASE_URL = process.env.REACT_APP_HOST_BASE_URL;

const FIELD_CLASS = "flex flex-col gap-1 text-xs font-semibold text-gray-500";
const INPUT_CLASS =
    "border border-gray-300 rounded-md px-2.5 py-2 text-sm font-normal text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500";

// Akfa nomidagi oxirgi qavs ichidagi qiymat — tekstura taxmini: "(A) Qanot (SW306G)" → "SW306G"
const guessTextureName = (akfaName) =>
    ((akfaName || "").match(/\(([^()]+)\)/g) || [])
        .map((part) => part.slice(1, -1).trim())
        .filter((part) => part !== "A" && part !== "Adt")
        .pop() || "";

const firstTextureId = (draft, textures, akfaName) => {
    const fromDraft = draft?.textureId ?? draft?.textureIds?.[0];
    if (fromDraft != null) return String(fromDraft);
    const guess = guessTextureName(akfaName).toLowerCase();
    const match = guess && textures.find((tx) => (tx.name || "").toLowerCase() === guess);
    return match ? String(match.id) : "";
};

// Akfa tovaridan yangi WinPro mahsuloti: product-draft bilan oldindan to'ldiriladi, create-product avtomatik bog'laydi
export default function AkfaCreateFromAkfaModal({ akfaProduct, onClose, onCreated }) {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);

    const [form, setForm] = useState(EMPTY_PRODUCT_FORM);
    const [textureId, setTextureId] = useState("");
    const [textures, setTextures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [draftRes, texturesRes] = await Promise.all([
                getAkfaProductDraft(token, akfaProduct.id),
                getList(`${BASE_URL}/v1/texture/list`, token),
            ]);
            if (cancelled) return;
            const textureList = texturesRes.success ? texturesRes.data || [] : [];
            const draft = draftRes.success && draftRes.data ? draftRes.data : {};
            setTextures(textureList);
            setForm((prev) => ({
                ...prev,
                ...Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== null && v !== undefined)),
            }));
            setTextureId(firstTextureId(draft, textureList, akfaProduct.name));
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [akfaProduct.id]);

    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name?.trim() || !textureId) {
            toast.warn(t("akfaIntegrationPage.createFromAkfa.required"), { position: "bottom-right" });
            return;
        }
        setSubmitting(true);
        // eslint-disable-next-line no-unused-vars
        const { textureIds, textureId: _ignored, ...product } = form;
        const response = await createProductFromAkfa(token, akfaProduct.id, {
            product: {
                ...product,
                name: form.name.trim(),
                article: (form.article || "").trim(),
                length: form.length === "" || form.length == null ? null : Number(form.length),
            },
            textureIds: [Number(textureId)],
        });
        setSubmitting(false);
        if (!response.success) {
            toast.error(response.message || t("akfaIntegrationPage.modal.error_generic"), { position: "bottom-right" });
            return;
        }
        const data = response.data || {};
        onCreated({
            productId: data.productId ?? data.id ?? null,
            productName: data.productName ?? data.name ?? form.name.trim(),
            article: data.article ?? form.article,
        });
    };

    return (
        <Dialog open as="div" className="relative z-50" onClose={onClose}>
            <div className="fixed inset-0 bg-slate-900/50" />
            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex items-center justify-center min-h-full p-5">
                    <Dialog.Panel className="w-full max-w-[540px] bg-white rounded-xl shadow-2xl overflow-hidden">
                        <div className="flex items-start justify-between gap-3 px-4 py-4 border-b border-gray-200">
                            <div className="min-w-0">
                                <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                                    {t("akfaIntegrationPage.createFromAkfa.title")}
                                </Dialog.Title>
                                <div className="text-xs text-gray-500 mt-0.5 truncate">
                                    Akfa: {akfaProduct.externalId ?? `#${akfaProduct.id}`} {akfaProduct.name}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label={t("akfaIntegrationPage.common.close")}
                                className="flex-none inline-flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:bg-slate-50 hover:text-gray-900"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 p-4">
                            <label className={`${FIELD_CLASS} sm:col-span-2`}>
                                {t("akfaIntegrationPage.createFromAkfa.name")} *
                                <input
                                    required
                                    autoFocus
                                    value={form.name || ""}
                                    onChange={(e) => setField("name", e.target.value)}
                                    disabled={loading}
                                    className={INPUT_CLASS}
                                />
                            </label>
                            <label className={FIELD_CLASS}>
                                {t("akfaIntegrationPage.createFromAkfa.article")}
                                <input
                                    value={form.article || ""}
                                    onChange={(e) => setField("article", e.target.value)}
                                    disabled={loading}
                                    className={INPUT_CLASS}
                                />
                            </label>
                            <label className={FIELD_CLASS}>
                                {t("akfaIntegrationPage.createFromAkfa.group")}
                                <select
                                    value={form.articleGroupType || ""}
                                    onChange={(e) => setField("articleGroupType", e.target.value)}
                                    disabled={loading}
                                    className={INPUT_CLASS}
                                >
                                    {ARTICLE_GROUP_TYPES.map((type) => (
                                        <option key={type} value={type}>
                                            {t(type)}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className={FIELD_CLASS}>
                                {t("akfaIntegrationPage.createFromAkfa.texture")} *
                                <select
                                    required
                                    value={textureId}
                                    onChange={(e) => setTextureId(e.target.value)}
                                    disabled={loading}
                                    className={INPUT_CLASS}
                                >
                                    <option value="">
                                        {loading
                                            ? t("akfaIntegrationPage.common.loading")
                                            : t("akfaIntegrationPage.mapping.texture_placeholder")}
                                    </option>
                                    {textures.map((tx) => (
                                        <option key={tx.id} value={tx.id}>
                                            {tx.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className={FIELD_CLASS}>
                                {t("akfaIntegrationPage.createFromAkfa.price")}
                                <input
                                    readOnly
                                    value={formatPrice(akfaProduct.currentRate, akfaProduct.currentRateCurrency)}
                                    className={`${INPUT_CLASS} bg-slate-50 text-gray-500`}
                                />
                            </label>
                            <p className="sm:col-span-2 m-0 text-xs text-gray-500">
                                {t("akfaIntegrationPage.createFromAkfa.note")}
                            </p>
                            <div className="sm:col-span-2 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-1 py-2 text-sm font-semibold text-gray-500 underline"
                                >
                                    {t("akfaIntegrationPage.common.cancel")}
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || submitting}
                                    className="px-4 py-2 text-sm font-semibold text-white bg-slate-800 rounded-md hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {submitting
                                        ? t("akfaIntegrationPage.modal.saving")
                                        : t("akfaIntegrationPage.createFromAkfa.submit")}
                                </button>
                            </div>
                        </form>
                    </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    );
}
